import { facebookPixelId1, facebookPixelId2 } from '@/utils';
import * as Sentry from '@sentry/react';
import { init } from '@web/common';
import store, { persistor } from '@web/common/src/store';
import { nativeApi } from '@web/common/src/utils/nativeApi';
import { sensorsTrack } from '@web/common/src/utils/shenceTrack';
import { isDev } from '@web/common/src/utils/util';
import 'dayjs/locale/zh-hk.js';
import { parse, stringify } from 'query-string';
import React, { StrictMode } from 'react';
import ReactDOM from 'react-dom';
import ReactPixel from 'react-facebook-pixel';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';
import { Provider } from 'react-redux';
import {
  BrowserRouter as Router,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import App from './App';
import './styles/index.less';
import { isEn } from './utils';
import { initLanguage } from './utils/languageutils';
import { VHKUnhandleRejection } from './utils/unhandleRejection';

nativeApi.init?.({
  initLangCallBack: initLanguage,
});

VHKUnhandleRejection();

// 初始化神策
sensorsTrack(import.meta.env.VITE_APP_SHENCE_SERVER_URL as string);

// 初始化各种埋点
const initTracking = () => {
  // 使用requestIdleCallback在浏览器空闲时初始化跟踪代码
  const initTrackingScripts = () => {
    // GA4
    ReactGA.initialize('G-Y92ZTVPBJ9');
    // fb pixel
    ReactPixel.init(facebookPixelId1);
    ReactPixel.init(facebookPixelId2);
    ReactPixel.pageView(); // For tracking page view
    // GTM
    const tagManagerArgs = {
      gtmId: 'GTM-5RSHVKS',
    };
    TagManager.initialize(tagManagerArgs);
  };

  // 使用requestIdleCallback在浏览器空闲时执行，如果不支持则降级为setTimeout
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(
      () => {
        initTrackingScripts();
      },
      { timeout: 2000 }
    ); // 设置2秒超时确保最终会执行
  } else {
    // 降级方案
    setTimeout(initTrackingScripts, 1000);
  }
};

const addLoadEvent = (fun: () => void) => {
  const oldLoad = window.onload as (arg: any) => void;
  if (typeof oldLoad !== 'function') {
    window.onload = fun;
  } else {
    window.onload = (arg: any) => {
      oldLoad && oldLoad(arg);
      fun();
    };
  }
};

if (!isDev()) {
  // 非开发环境初始化跟踪
  addLoadEvent(() => {
    initTracking();
  });
}

if (!isDev() && String(import.meta.env.VITE_APP_STARTSENTRY) === 'true') {
  // 定义匹配类型
  type MatchType = 'exact' | 'prefix';

  // 定义路径配置结构
  interface PathConfig {
    path: string;
    type: MatchType;
  }

  // 不进行上报的页面路径配置列表
  const ignorePathConfigs: PathConfig[] = [
    { path: '/pub/CashLoan/', type: 'prefix' }, // 前缀匹配
    { path: '/pub/ClearCardCredit/', type: 'prefix' }, // 前缀匹配
    { path: '/vhk/clCredit', type: 'exact' }, // 精确匹配
    { path: '/vhk/credit', type: 'exact' }, // 精确匹配
    // 可以在此添加更多路径，例如:
    // { path: '/exact/path', type: 'exact' }, // 精确匹配
    // { path: '/prefix/path/', type: 'prefix' }, // 前缀匹配
  ];

  // 检查当前页面是否应该被忽略
  const shouldIgnoreSentry = () => {
    const currentPath = window.location.pathname;
    // 检查当前路径是否匹配任何一个需要忽略的路径配置
    return ignorePathConfigs.some((config) => {
      if (config.type === 'exact') {
        return currentPath === config.path;
      }
      return currentPath.startsWith(config.path);
    });
  };

  // 如果当前页面不应该被忽略，则初始化Sentry
  if (!shouldIgnoreSentry()) {
    Sentry.init({
      dsn: import.meta.env.VITE_APP_SENTRY_DSN,
      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        Sentry.httpClientIntegration(),
        Sentry.replayIntegration(),
      ],
      // app_js_bridge:true,
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 1,
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      release: import.meta.env.VITE_APP_RELEASE,
      beforeSend(event) {
        // 再次检查当前页面是否应该被忽略（以防页面发生变化）
        if (shouldIgnoreSentry()) {
          return null; // 返回null将阻止事件发送
        }
        return event;
      },
      environment: import.meta.env.VITE_APP_ENV,
      normalizeDepth: 5,
      debug: import.meta.env.VITE_APP_ENV === 'development',
    });

    // 添加路由变化监听器，在URL变化时重新检查是否应该禁用Sentry
    const disableSentryOnNavigation = () => {
      if (shouldIgnoreSentry()) {
        // 禁用所有Sentry事件
        Sentry.setTag('ignored_page', 'true');
        // 将采样率设为0，完全停止采样
        const scope = Sentry.getCurrentScope();
        if (scope) {
          scope.setTransactionName('ignored_transaction');
        }
      } else {
        Sentry.setTag('ignored_page', 'false');
      }
    };

    // 初始检查
    disableSentryOnNavigation();

    // 监听路由变化
    const originalPushState = history.pushState;
    history.pushState = function (...args) {
      originalPushState.apply(this, args);
      disableSentryOnNavigation();
    };

    window.addEventListener('popstate', disableSentryOnNavigation);

    init({
      logErrors: true,
    });
  } else {
    // 如果当前页面应该被忽略，设置一个空的Sentry对象以避免错误
    console.log('Sentry disabled for this page');
  }
}

document.body.className = `${document.body.className} ${isEn() ? 'en' : ''}`;

ReactDOM.render(
  <StrictMode>
    <Router>
      <QueryParamProvider
        adapter={ReactRouter6Adapter}
        options={{
          searchStringToObject: parse,
          objectToSearchString: stringify,
        }}
      >
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <App />
          </PersistGate>
        </Provider>
      </QueryParamProvider>
    </Router>
  </StrictMode>,
  document.querySelector('#root')
);
